import React, { useState, useEffect } from 'react'
import { Eye, Download, Truck, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import AdminFilters from '../components/filters/AdminFilters'
import AdminPagination from '../components/pagination/AdminPagination'
import { useAdminFilters } from '../hooks/useAdminFilters'
import DataTable, { Column } from '../components/DataTable'
import { renderStatusBadge, renderPrice, renderDate } from '../utils/tableRenderers'
import { api } from '../lib/api'
import toast from 'react-hot-toast'
import { extractErrorMessage } from '../utils/errorHandler'

interface OrderItem {
  id: string
  productId: string
  product: { name: string; price: number }
  quantity: number
  price: number
}

interface Order {
  id: string
  orderNumber: string
  user: { email: string; firstName: string; lastName: string }
  totalAmount: number
  status: string
  shippingStatus?: string
  paymentStatus: string
  courierName?: string
  awbCode?: string
  trackingUrl?: string
  createdAt: string
  items: OrderItem[]
}

const SHIPMENT_FILTERS = [
  { value: 'PENDING_SHIPMENT', label: 'Pending Shipment' },
  { value: 'SHIPMENT_CREATED', label: 'Shipment Created' },
  { value: 'PICKUP_PENDING', label: 'Pickup Pending' },
  { value: 'SHIPPED', label: 'Shipped' },
  { value: 'IN_TRANSIT', label: 'In Transit' },
  { value: 'OUT_FOR_DELIVERY', label: 'Out For Delivery' },
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'CANCELLED', label: 'Cancelled' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'REFUND_PENDING', label: 'Refund Pending' },
  { value: 'REFUNDED', label: 'Refunded' },
]

export default function OrdersPage() {
  const navigate = useNavigate()
  const filterManager = useAdminFilters({ defaultLimit: 10 })
  const { search, debouncedSearch, page, limit, hasActiveFilters, setSearch, setPage, setLimit, setFilter, removeFilter, clearFilters } = filterManager

  const [orders, setOrders] = useState<Order[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)

  const statusFilter = filterManager.filters.status || ''

  useEffect(() => {
    fetchOrders()
  }, [page, limit, debouncedSearch, JSON.stringify(filterManager.filters)])

  const fetchOrders = async () => {
    try {
      setLoading(true)
      const response = await api.getOrders(page, limit, statusFilter, debouncedSearch, filterManager.filters)
      setOrders(response.data.data || [])
      setTotal(response.data.pagination?.total || 0)
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error)
      toast.error(errorMessage)
      console.error('Fetch orders error:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExportOrders = async () => {
    try {
      const response = await api.exportOrders(statusFilter)
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `orders-${new Date().toISOString().split('T')[0]}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Orders exported successfully')
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error)
      toast.error(errorMessage || 'Failed to export orders')
    }
  }

  const columns: Column<Order>[] = [
    {
      key: 'orderNumber',
      header: 'Order ID',
      sortable: true,
      render: (val, row) => (
        <div className="font-semibold text-gray-900">
          #{val}
          <div className="text-xs text-gray-500 font-normal">{row.items?.length || 0} items</div>
        </div>
      ),
    },
    {
      key: 'user',
      header: 'Customer',
      render: (val) => (
        <div>
          <div className="font-medium text-gray-900">{val ? `${val.firstName || ''} ${val.lastName || ''}`.trim() || val.email : 'Guest'}</div>
          {val?.email && <div className="text-xs text-gray-500">{val.email}</div>}
        </div>
      ),
    },
    {
      key: 'totalAmount',
      header: 'Total',
      sortable: true,
      render: (val) => renderPrice(val),
    },
    {
      key: 'status',
      header: 'Order Status',
      render: (val) => renderStatusBadge(val),
    },
    {
      key: 'paymentStatus',
      header: 'Payment',
      render: (val) => renderStatusBadge(val),
    },
    {
      key: 'courierName',
      header: 'Logistics',
      render: (_, row) => (
        <div className="text-xs">
          {row.courierName ? (
            <div className="font-medium text-gray-900 flex items-center gap-1">
              <Truck size={12} className="text-[#6D3A0D]" />
              {row.courierName}
            </div>
          ) : (
            <span className="text-gray-400">Unassigned</span>
          )}
          {row.awbCode && (
            <div className="text-gray-500 flex items-center gap-1 mt-0.5">
              <span>AWB: {row.awbCode}</span>
              {row.trackingUrl && (
                <a href={row.trackingUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                  <ExternalLink size={10} />
                </a>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      render: (val) => renderDate(val),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (_, row) => (
        <button
          onClick={() => navigate(`/orders/${row.id}`)}
          className="flex items-center gap-1 bg-[#6D3A0D] text-white px-3 py-1.5 rounded text-xs hover:bg-[#5D2A0D] transition font-medium"
        >
          <Eye size={14} /> View
        </button>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">Orders & Shipments</h1>
          <p className="text-sm text-gray-600">Manage orders, courier assignments, tracking and Shiprocket logistics</p>
        </div>
        <button
          onClick={handleExportOrders}
          className="flex items-center justify-center gap-2 bg-[#6D3A0D] text-white px-4 py-2 rounded-lg hover:bg-[#5D2A0D] transition w-full sm:w-auto font-medium"
        >
          <Download size={18} />
          <span>Export CSV</span>
        </button>
      </div>

      <AdminFilters
        search={search}
        searchPlaceholder="Search orders by ID or customer email..."
        onSearchChange={setSearch}
        filters={filterManager.filters}
        onFilterChange={setFilter}
        onRemoveFilter={removeFilter}
        onClearFilters={clearFilters}
        hasActiveFilters={hasActiveFilters}
        isLoading={loading}
        fields={[
          {
            key: 'status',
            label: 'Order Status',
            type: 'select',
            options: SHIPMENT_FILTERS,
          },
          {
            key: 'paymentStatus',
            label: 'Payment Status',
            type: 'select',
            options: [
              { label: 'Pending', value: 'PENDING' },
              { label: 'Completed', value: 'COMPLETED' },
              { label: 'Failed', value: 'FAILED' },
              { label: 'Refunded', value: 'REFUNDED' },
            ],
          },
          {
            key: 'startDate',
            label: 'Start Date',
            type: 'date',
          },
          {
            key: 'endDate',
            label: 'End Date',
            type: 'date',
          },
        ]}
      />

      <DataTable
        columns={columns}
        data={orders}
        loading={loading}
        emptyMessage="No orders found matching the criteria"
        onRowClick={(row) => navigate(`/orders/${row.id}`)}
      />

      {total > 0 && (
        <div className="mt-6">
          <AdminPagination
            page={page}
            limit={limit}
            total={total}
            onPageChange={setPage}
            onLimitChange={setLimit}
            isLoading={loading}
            showInfo={true}
          />
        </div>
      )}
    </div>
  )
}
